import { ShareControllerInterface } from "@share/live-kit/interfaces/ShareControllerInterface";
import { SharingTypes } from "@api/enums/WebConferenceSlides/SharingTypes";
import { WebConferenceService } from "@share/web-conferences/data/WebConferenceService";
import { LiveKitClientInterface } from "@share/live-kit/interfaces/LiveKitClientInterface";
import { StoreHelper } from "@share/web-conferences/helpers/StoreHelper";
import { createLocalScreenTracks, LocalTrack, LocalVideoTrack, ScreenSharePresets, Track } from "livekit-client";
import Source = Track.Source;

export class LiveKitShareController implements ShareControllerInterface {
    buttonMode: boolean;
    canSharePdf: boolean;
    showLibrary = false;
    isSharingBeginStopped = false;
    isSharingSwitching = false;

    constructor(
        private readonly webConferenceService: WebConferenceService,
        public readonly client: LiveKitClientInterface,
        private readonly userId: number,
        private readonly storeHelper: StoreHelper
    ) {

    }

    get isSharing() {
        return this.webConferenceService.webConferenceData?.currentPresentation?.userId === this.userId;
    }

    get isSharingScreen() {
        return Boolean(this.isSharing && this.webConferenceService.webConferenceData?.hasSharingType(SharingTypes.Screen));
    }

    get isCurrentPresentationActive() {
        return this.webConferenceService.webConferenceData?.currentPresentation != null;
    }

    get isThereSharingUser() {
        return Boolean(!this.isSharing && this.webConferenceService.webConferenceData?.isSharing());
    }

    get webConferenceData() {
        return this.webConferenceService.webConferenceData;
    }

    get isThereSharingTracks() {
        return Object.values(this.client.tracksManager.tracks).findIndex(item => item.video?.source == Track.Source.ScreenShare) >= 0;
    }

    async onShare(type: SharingTypes): Promise<void> {
        if (type == SharingTypes.Screen) {
            await this.shareScreen();
        } else {
            this.onSharePdf();
        }
    }

    onSharePdf() {
        this.showLibrary = true;
    }

    sharePdf(): Promise<void> {
        return Promise.resolve(undefined);
    }

    async shareScreen(): Promise<void> {
        if (this.isSharingSwitching) {
            return;
        }

        this.isSharingSwitching = true;

        try {
            // this.client?.room.localParticipant.setScreenShareEnabled(true);
            //@todo move to client
            const tracks = await createLocalScreenTracks({
                audio: true,
                resolution: ScreenSharePresets.h1080fps30.resolution
            });

            const tasks = [];

            for (const track of tracks) {
                tasks.push(this.client?.room.localParticipant.publishTrack(track, {
                    source: track instanceof LocalVideoTrack ? Source.ScreenShare : Source.ScreenShareAudio,
                    simulcast: false
                }));

                if (track instanceof LocalVideoTrack) {
                    track.mediaStreamTrack.onended = () => {
                        console.log('share track ended');
                        this.stopSharing();
                    }
                }
            }

            await Promise.all(tasks);

            await this.webConferenceService.changeCurrentPresentation({
                type: SharingTypes.Screen,
                userId: this.userId as number,
                data: {} as any
            });
        } catch (e) {
            await this.storeHelper.store.dispatch('reportError', e);

            if (this.webConferenceService.webConferenceData?.hasSharingType(SharingTypes.Screen)) {
                await this.webConferenceService.nullCurrentPresentation();
            }
        }
        finally {
            this.isSharingSwitching = false;
        }
    }

    async stopSharing(): Promise<void> {
        console.trace('stop sharing');

        if (this.isSharingBeginStopped) { return; }

        this.isSharingBeginStopped = true;

        try {

            if (this.client.room.localParticipant.isScreenShareEnabled) {
                const tracks: LocalTrack[] = this.client.room.localParticipant.getTracks()
                    .filter(item => (item.source == Source.ScreenShare || item.source == Source.ScreenShareAudio) && item.track)
                    .map(item => item.track as LocalTrack);

                this.client.room.localParticipant.unpublishTracks(tracks);
            }

            if (this.webConferenceService.webConferenceData?.currentPresentation != null) {
                await this.webConferenceService.nullCurrentPresentation();
            }
        }
        finally {
            this.isSharingBeginStopped = false;
        }

    }
}
